export type MigrationEntry = { uuid: string; pause: boolean };

export type MigrationCard = { uuid: string; startEntryUuid: string | null; endEntryUuid: string | null };

/** Zuordnung alte Stunde -> neue Stunde. Alte Stunden ohne Eintrag haben kein Ziel. */
export type EntryMapping = Record<string, string>;

/**
 * Vorschlag für die Zuordnung: die n-te Unterrichtsstunde des bisherigen Rasters wird zur n-ten
 * Unterrichtsstunde des neuen Rasters, die n-te Pause zur n-ten Pause.
 */
export function buildDefaultMapping(oldEntries: MigrationEntry[], newEntries: MigrationEntry[]): EntryMapping {
  const mapping: EntryMapping = {};
  [false, true].forEach((pause) => {
    const targets = newEntries.filter((entry) => entry.pause === pause);
    oldEntries
      .filter((entry) => entry.pause === pause)
      .forEach((entry, index) => {
        const target = targets[index];
        if (target) mapping[entry.uuid] = target.uuid;
      });
  });
  return mapping;
}

/**
 * Vorschau der Kartenmigration – spiegelt die Regeln des Backends (`planCardMigration`):
 * Eine Karte bleibt nur gesteckt, wenn jede ihrer Unterrichtsstunden ein Ziel hat und die Ziele im
 * neuen Raster lückenlos aufeinander folgen (Pausen dazwischen sind erlaubt). In Pausen lassen sich
 * auf der Stecktafel keine Karten stecken: beginnt oder endet eine Karte auf einer Stunde, deren Ziel
 * eine Pause ist, wandert sie in den Kartenvorrat. Wird nur eine Stunde mitten in einer Mehrfachstunde
 * zur Pause, bleibt die Karte gesteckt und überspannt sie.
 * `oldEntries`/`newEntries` sind chronologisch sortiert.
 */
export function previewCardMigration({
  cards,
  mapping,
  newEntries,
  oldEntries,
}: {
  cards: MigrationCard[];
  mapping: EntryMapping;
  newEntries: MigrationEntry[];
  oldEntries: MigrationEntry[];
}): { migrated: number; unplaced: number } {
  const oldIndexByUuid = new Map(oldEntries.map((entry, index) => [entry.uuid, index]));
  const newIndexByUuid = new Map(newEntries.map((entry, index) => [entry.uuid, index]));
  const pauseUuids = new Set(newEntries.filter((entry) => entry.pause).map((entry) => entry.uuid));

  const staysPlaced = (card: MigrationCard): boolean => {
    const startIndex = card.startEntryUuid ? oldIndexByUuid.get(card.startEntryUuid) : undefined;
    const endIndex = card.endEntryUuid ? oldIndexByUuid.get(card.endEntryUuid) : startIndex;
    if (startIndex === undefined || endIndex === undefined || endIndex < startIndex) return false;

    const lessons = oldEntries.slice(startIndex, endIndex + 1).filter((entry) => !entry.pause);
    if (lessons.length === 0) return false;
    const allTargets = lessons.map((entry) => mapping[entry.uuid]);
    if (allTargets.some((uuid) => !uuid)) return false;
    if (pauseUuids.has(allTargets[0]) || pauseUuids.has(allTargets[allTargets.length - 1])) return false;
    const targets = allTargets.filter((uuid) => !pauseUuids.has(uuid));
    const indexes = targets.map((uuid) => newIndexByUuid.get(uuid) ?? -1);
    if (indexes.some((index, i) => index === -1 || (i > 0 && index <= indexes[i - 1]))) return false;

    const targetSet = new Set(targets);
    return newEntries
      .slice(indexes[0], indexes[indexes.length - 1] + 1)
      .every((entry) => entry.pause || targetSet.has(entry.uuid));
  };

  const migrated = cards.filter(staysPlaced).length;
  return { migrated, unplaced: cards.length - migrated };
}

/** Anzahl gesteckter Karten je Stunde des bisherigen Rasters (Mehrfachstunden zählen in jeder ihrer Stunden). */
export function countCardsPerEntry(cards: MigrationCard[], oldEntries: MigrationEntry[]): Map<string, number> {
  const oldIndexByUuid = new Map(oldEntries.map((entry, index) => [entry.uuid, index]));
  const counts = new Map<string, number>();
  cards.forEach((card) => {
    const startIndex = card.startEntryUuid ? oldIndexByUuid.get(card.startEntryUuid) : undefined;
    const endIndex = card.endEntryUuid ? oldIndexByUuid.get(card.endEntryUuid) : startIndex;
    if (startIndex === undefined || endIndex === undefined) return;
    oldEntries.slice(startIndex, endIndex + 1).forEach((entry) => {
      counts.set(entry.uuid, (counts.get(entry.uuid) ?? 0) + 1);
    });
  });
  return counts;
}
